'use client'

import { Menu, Transition } from '@headlessui/react'
import { cn } from '~/utils/cn'
import Link from 'next/link'
import { IconCaret } from './Icons/IconCaret'
// import { ResetFlowButton } from './Frigade/ResetFlowButton'
import { ReactNode, useMemo } from 'react'
import { useTranslation } from '~/i18n/useTranslation'

export type AccountMenuItem = AccountMenuItemLink | AccountMenuItemButton

type AccountMenuItemLink = {
  type: 'link' | 'api-link'
  url: string
  label: string
  external?: boolean
}

type AccountMenuItemButton = {
  type: 'button'
  name: string
  label: string
}

type AccountMenuProps = {
  menuItems?: AccountMenuItem[]
  children: ReactNode
}

export const AccountMenu = ({
  menuItems,
  children,
}: AccountMenuProps) => {
  const { t } = useTranslation()
  const resolvedMenuItems = useMemo(() => menuItems ?? [
    { type: 'link' as const, url: '/', label: t('nav.dashboard') },
    { type: 'link' as const, url: '/account', label: t('nav.manageAccount') },
    { type: 'link' as const, url: '/workspace', label: t('nav.manageWorkspace') },
    { type: 'link' as const, url: 'https://taloflow.ai/walkthrough-video', label: t('nav.playWalkthrough'), external: true },
    { type: 'link' as const, url: 'https://docs.taloflow.ai', label: t('nav.documentation'), external: true },
    { type: 'link' as const, url: 'https://www.taloflow.ai/help', label: t('nav.support'), external: true },
    { type: 'api-link' as const, url: '/auth/logout', label: t('nav.logout') },
  ], [menuItems, t])
  return (
    <div className="relative">
      <Menu>
        {({ open }) => (
          <>
            <Menu.Button className="rounded-md space-x-2 flex justify-center items-center">
              {children}
              <IconCaret className="text-stone-700 w-2 h-2" />
            </Menu.Button>
            <Transition
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 w-56 mt-2 origin-top-right bg-white border border-gray-200 last:divide-y last:divide-gray-100 rounded-md shadow-lg outline-none">
                {resolvedMenuItems.map((menuItem) => (
                  <Menu.Item
                    key={
                      menuItem.type === 'button' ? menuItem.name : menuItem.url
                    }
                  >
                    {({ active }) => {
                      if (menuItem.type === 'link') {
                        return (
                          <Link
                            href={menuItem.url}
                            target={menuItem.external ? '_blank' : undefined}
                            rel={
                              menuItem.external
                                ? 'noopener noreferrer'
                                : undefined
                            }
                            className={cn(
                              'flex w-full px-4 py-2 text-sm leading-5 text-left',
                              {
                                'bg-gray-100 text-gray-900': active,
                                'text-gray-700 hover:bg-gray-100': !active,
                              }
                            )}
                          >
                            {menuItem.label}
                          </Link>
                        )
                      }
                      if (menuItem.type === 'api-link') {
                        return (
                          <a
                            href={menuItem.url}
                            className={cn(
                              'flex w-full px-4 py-2 text-sm leading-5 text-left',
                              {
                                'bg-gray-100 text-gray-900': active,
                                'text-gray-700 hover:bg-gray-100': !active,
                              }
                            )}
                          >
                            {menuItem.label}
                          </a>
                        )
                      }
                      // if (menuItem.type === 'button' && menuItem.name === 'walkthrough') {
                      //   return (
                      //     <ResetFlowButton
                      //       className={cn(
                      //         'flex w-full px-4 py-2 text-sm leading-5 text-left',
                      //         {
                      //           'bg-gray-100 text-gray-900': active,
                      //           'text-gray-700 hover:bg-gray-100': !active,
                      //         }
                      //       )}
                      //     >
                      //       {menuItem.label}
                      //     </ResetFlowButton>
                      //   )
                      // }
                      return (
                        <button
                          type="button"
                          // onClick={() => {
                          //   if (menuItem.name === 'walkthrough') {
                          //     setWalkthroughOpen(true)
                          //   }
                          // }}
                          className={cn(
                            'flex w-full px-4 py-2 text-sm leading-5 text-left',
                            {
                              'bg-gray-100 text-gray-900': active,
                              'text-gray-700 hover:bg-gray-100': !active,
                            }
                          )}
                        >
                          {menuItem.label}
                        </button>
                      )
                    }}
                  </Menu.Item>
                ))}
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
      <div id="user-menu-getting-started" className="absolute bottom-0" />
    </div>
  )
}
